import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environment/environment';

export interface MenderArtifact {
	id: string;
	name: string;
	description: string;
	device_types_compatible: string[];
	size: number;
	tags: Record<string, string>;
}

export interface MenderArtifactListResponse {
	artifacts: MenderArtifact[];
	totalCount: number;
}

export interface MenderDeviceStatus {
	id: string;
	status: string;
	updated_ts: string;
}

export interface MenderDeploymentStatus {
	id: string;
	status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'ALREADY_INSTALLED' | 'ERROR';
	menderStatus?: string;
	menderResult?: string;
	devices: MenderDeviceStatus[];
}

export interface MenderDeployment {
	id: string;
	machineId: string;
	userAccountId: string;
	serialNumber: string;
	family: string;
	menderDeploymentId: string;
	menderDeviceId: string;
	menderSerial: string;
	artifactName: string;
	artifactDeviceType: string | null;
	status: string;
	deploymentNotes: string | null;
	notes: string | null;
	deploymentDate: string;
	scheduledAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface MenderDeploymentListResponse {
	data: MenderDeployment[];
	total: number;
}

@Injectable({ providedIn: 'root' })
export class MenderService {
	private endpoint = environment.apiEndpoint;

	constructor(private http: HttpClient) {}

	listArtifacts(page?: number, perPage?: number, deviceType?: string, namePrefix?: string): Observable<MenderArtifactListResponse> {
		let params = new HttpParams();
		if (page !== undefined) params = params.set('page', page.toString());
		if (perPage !== undefined) params = params.set('per_page', perPage.toString());
		if (deviceType) params = params.set('device_type', deviceType);
		if (namePrefix) params = params.set('name_prefix', namePrefix);

		return this.http
			.get<{ artifacts: MenderArtifact[]; totalCount: number }>(`${this.endpoint}/v1/mender/artifacts`, {
				params,
				observe: 'response',
			})
			.pipe(
				map((response) => ({
					artifacts: response.body?.artifacts || [],
					totalCount: response.body?.totalCount ?? parseInt(response.headers.get('X-Total-Count') || '0', 10),
				}))
			);
	}

	getDeploymentStatus(menderDeploymentId: string, portalDeploymentId?: string): Observable<MenderDeploymentStatus> {
		let params = new HttpParams().set('deployment_id', menderDeploymentId);
		if (portalDeploymentId) params = params.set('portal_deployment_id', portalDeploymentId);
		return this.http.get<MenderDeploymentStatus>(`${this.endpoint}/v1/mender/deployment/status`, { params });
	}

	listDeployments(params?: {
		status?: string;
		serialNumber?: string;
		search?: string;
		limit?: number;
		offset?: number;
		orderColumn?: string;
		orderDirection?: 'asc' | 'desc';
	}): Observable<MenderDeploymentListResponse> {
		let httpParams = new HttpParams();
		if (params?.status) httpParams = httpParams.set('status', params.status);
		if (params?.serialNumber) httpParams = httpParams.set('serialNumber', params.serialNumber);
		if (params?.search) httpParams = httpParams.set('search', params.search);
		if (params?.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
		if (params?.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString());
		if (params?.orderColumn) httpParams = httpParams.set('orderColumn', params.orderColumn);
		if (params?.orderDirection) httpParams = httpParams.set('orderDirection', params.orderDirection);

		return this.http.get<MenderDeploymentListResponse>(`${this.endpoint}/v1/mender/deployments`, { params: httpParams });
	}

	getDeploymentDetails(menderDeploymentId: string): Observable<any> {
		const params = new HttpParams().set('mender_deployment_id', menderDeploymentId);
		return this.http.get<any>(`${this.endpoint}/v1/mender/deployment/details`, { params });
	}

	cancelDeployment(portalDeploymentId: string): Observable<{ message: string }> {
		return this.http.post<{ message: string }>(`${this.endpoint}/v1/mender/deployments/cancel`, {
			deployment_id: portalDeploymentId,
		});
	}
}
